import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { m } from "@/paraglide/messages.js";

interface ErrorBoundaryProps {
  children: ReactNode;
  errorView?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class AppErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[AppErrorBoundary] Caught error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.errorView) return this.props.errorView;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-md rounded-lg border bg-card p-6 text-card-foreground">
          <h1 className="mb-2 text-2xl font-semibold">{m.error_boundary_title()}</h1>
          <p className="text-sm text-muted-foreground">
            {m.error_boundary_description()}
          </p>
          {this.state.error ? (
            <details className="mt-4 text-xs text-muted-foreground">
              <summary className="cursor-pointer">{m.error_boundary_details()}</summary>
              <pre className="mt-2 overflow-auto rounded bg-muted p-2">
                {this.state.error.message}
              </pre>
            </details>
          ) : null}
          <Button className="mt-4" onClick={() => window.location.reload()}>
            {m.error_boundary_reload_app()}
          </Button>
        </div>
      </div>
    );
  }
}

export class RouteErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[RouteErrorBoundary] Caught error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private readonly handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.errorView) return this.props.errorView;

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="w-full max-w-md space-y-3 rounded-lg border bg-card p-6 text-card-foreground">
          <h2 className="text-xl font-semibold">{m.route_error_title()}</h2>
          <p className="text-sm text-muted-foreground">
            {m.route_error_description()}
          </p>
          {this.state.error ? (
            <p className="text-sm text-destructive">{this.state.error.message}</p>
          ) : null}
          <div className="flex gap-2">
            <Button onClick={this.handleRetry}>{m.try_again()}</Button>
            <Button variant="outline" onClick={() => window.history.back()}>
              {m.go_back()}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export function RouteSkeleton() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-7 w-44" />
      <Skeleton className="h-4 w-80 max-w-full" />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Skeleton className="h-60 w-full" />
    </div>
  );
}
